/**
 * @file `PushClient` — the browser-side Web Push opt-in for the capacity
 *       queue's "your spot is ready" callback (umbrella #1257 Part E, #1284).
 * @module @labcraft/lab/ui/lib/push-client
 *
 * The SPA side of the same feature `src/service/push.ts` serves. When a learner
 * hits the at-capacity wall, {@link AtCapacityOverlay} offers to enable desktop
 * notifications; clicking it calls {@link PushClient.enable}, which:
 *   1. registers the static `/push-sw.js` service worker (served from the
 *      workspace `public/` dir at the site root),
 *   2. requests `Notification` permission,
 *   3. subscribes to push with the daemon's VAPID public key, and
 *   4. POSTs the resulting `PushSubscription` to `POST /api/push/subscribe`
 *      (cookie-authenticated) so `Push.notify` can reach this browser.
 *
 * Everything is wrapped so nothing throws uncaught — `enable` resolves `false`
 * on any failure (permission denied, push unsupported, a network error), which
 * the overlay renders as the "keep this tab open" fallback. Feature detection
 * ({@link PushClient.isSupported}) gates the opt-in so unsupported browsers
 * never see the button.
 */

import type { PushSubscriptionInput } from '../../shared/routes.ts'

/**
 * Static browser-side Web Push helpers. All methods are `static` — there's no
 * per-instance state; the class is the discovery surface for "everything the
 * SPA does with Web Push" per the functions-on-classes rule.
 */
export class PushClient {
  /**
   * Whether this browser supports the Web Push opt-in — a Service Worker, the
   * `PushManager` API, and the `Notification` API must all be present. The
   * overlay gates the enable button on this so an unsupported browser only ever
   * sees the "keep this tab open" fallback.
   *
   * @returns `true` when Web Push is usable in this browser.
   */
  static isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      typeof window !== 'undefined' &&
      'PushManager' in window &&
      'Notification' in window
    )
  }

  /**
   * Register the service worker, request notification permission, subscribe to
   * push with the daemon's VAPID key, and persist the subscription server-side.
   * Never throws — resolves `false` on any failure (push unsupported, permission
   * denied or dismissed, subscribe or network error) so the overlay can fall
   * back to the keep-tab-open message.
   *
   * @param vapidPublicKey - The base64url VAPID public key from
   *   `GET /api/push/vapid`. An empty string means push is dark; the caller
   *   should not reach here in that case, but it's handled defensively.
   * @returns `true` when the subscription was created and stored; `false`
   *   otherwise.
   */
  static async enable(vapidPublicKey: string): Promise<boolean> {
    if (!PushClient.isSupported() || vapidPublicKey === '') return false

    try {
      const registration = await navigator.serviceWorker.register('/push-sw.js')

      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return false

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: PushClient.urlBase64ToUint8Array(vapidPublicKey),
      })

      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(subscription.toJSON() as PushSubscriptionInput),
      })

      return response.ok
    } catch {
      // Any failure (unsupported, denied, network, quota) collapses to the
      // keep-tab-open fallback — the learner is never blocked by a push error.
      return false
    }
  }

  /**
   * Enable Web Push AND opt the learner into a "your lab time is back" push
   * when their daily free budget next resets (#1315) — the out-of-time wall's
   * opt-in. Runs the full {@link enable} flow first (register → permission →
   * subscribe → store); only on success does it POST the resume-reminder opt-in,
   * so a learner we can't actually reach is never flagged. Never throws —
   * resolves `false` on any failure, which the wall renders as its static
   * reset-clock fallback.
   *
   * @param vapidPublicKey - The base64url VAPID key from `GET /api/push/vapid`.
   * @returns `true` when subscribed AND opted in; `false` otherwise.
   */
  static async enableResumeReminder(vapidPublicKey: string): Promise<boolean> {
    if (!(await PushClient.enable(vapidPublicKey))) return false

    try {
      const response = await fetch('/api/push/resume-reminder', {
        method: 'POST',
        credentials: 'include',
      })
      return response.ok
    } catch {
      return false
    }
  }

  /**
   * Convert a base64url VAPID public key into the `Uint8Array` that
   * `pushManager.subscribe`'s `applicationServerKey` requires — restore base64
   * padding, swap the url-safe alphabet back, then decode each byte.
   *
   * @param base64 - The base64url-encoded VAPID public key.
   * @returns The decoded key bytes, backed by a plain `ArrayBuffer` so the
   *   result satisfies `BufferSource` for `applicationServerKey` (a
   *   `Uint8Array<ArrayBufferLike>` isn't assignable — the buffer could be a
   *   `SharedArrayBuffer`).
   */
  private static urlBase64ToUint8Array(
    base64: string,
  ): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4)
    const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')

    const raw = atob(normalized)
    const bytes = new Uint8Array(new ArrayBuffer(raw.length))
    for (let i = 0; i < raw.length; i++) {
      bytes[i] = raw.charCodeAt(i)
    }
    return bytes
  }
}
